import React, { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { StatusBadge, AuthImage, usePoll, fmtUSDT } from "@/components/common";

export default function AdminDeposits() {
  const [deposits, setDeposits] = useState([]);
  const [filter, setFilter] = useState("Pending Verification");
  const [active, setActive] = useState(null);
  const [verifiedAmount, setVerifiedAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    const q = filter ? `?status=${encodeURIComponent(filter)}` : "";
    api.get(`/admin/deposits${q}`).then((r) => setDeposits(r.data)).catch(() => {});
  };
  usePoll(load, 6000, [filter]);

  const review = async (action) => {
    setBusy(true);
    try {
      await api.post(`/admin/deposits/${active.id}/review`, { action, reason, verified_amount: verifiedAmount || active.amount });
      toast.success(action === "approve" ? "Deposit approved & credited" : "Deposit rejected");
      setActive(null); setReason(""); setVerifiedAmount(""); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Deposit Verifications</h1>
      <div className="flex gap-2 flex-wrap">
        {["Pending Verification", "Approved", "rejected", ""].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`deposit-filter-${f || "all"}`}
            className={`px-3 py-1.5 rounded-full text-sm font-medium ${filter === f ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
            {f || "All"}
          </button>
        ))}
      </div>

      {deposits.length === 0 ? <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-400">No deposits.</div> : (
        <div className="space-y-3" data-testid="admin-verification-queue-table">
          {deposits.map((d) => (
            <div key={d.id} className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col sm:flex-row gap-4 justify-between">
              <div className="flex gap-4">
                {d.file_id && <AuthImage fileId={d.file_id} className="w-20 h-20 rounded-lg object-cover border border-slate-200" />}
                <div>
                  <div className="font-mono text-sm text-slate-900">{d.deposit_ref}</div>
                  <div className="text-sm text-slate-600">{d.customer_name} · {fmtUSDT(d.amount)} USDT · {d.network}</div>
                  <div className="font-mono text-xs text-slate-400 break-all max-w-md mt-0.5">TXID: {d.txid}</div>
                  {d.flagged && <span className="text-xs text-rose-600 font-semibold">⚠ Duplicate evidence flagged</span>}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <StatusBadge status={d.status} />
                {d.status === "Pending Verification" && (
                  <button onClick={() => { setActive(d); setVerifiedAmount(d.amount); }} data-testid={`review-deposit-${d.deposit_ref}`}
                    className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">Review</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40" onClick={() => setActive(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full space-y-4" onClick={(e) => e.stopPropagation()} data-testid="deposit-review-modal">
            <h2 className="font-heading font-bold text-lg">Review {active.deposit_ref}</h2>
            {active.file_id && <AuthImage fileId={active.file_id} className="w-full max-h-64 object-contain rounded-xl border border-slate-200" />}
            <div className="text-sm text-slate-600">Claimed: {fmtUSDT(active.amount)} USDT · {active.network}</div>
            <div className="font-mono text-xs text-slate-400 break-all">TXID: {active.txid}</div>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Verified amount (USDT)</span>
              <input value={verifiedAmount} onChange={(e) => setVerifiedAmount(e.target.value)} data-testid="verified-amount-input"
                className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 font-mono outline-none" />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Reason (required to reject)</span>
              <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason-input"
                className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 outline-none" />
            </label>
            <div className="flex gap-2">
              <button onClick={() => review("approve")} disabled={busy} data-testid="approve-deposit-button"
                className="flex-1 px-4 py-2.5 rounded-xl bg-emerald-600 text-white font-semibold disabled:opacity-60">Approve & credit</button>
              <button onClick={() => review("reject")} disabled={busy || !reason.trim()} data-testid="reject-deposit-button"
                className="flex-1 px-4 py-2.5 rounded-xl border-2 border-rose-300 text-rose-700 font-semibold disabled:opacity-60">Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
